import React, { useCallback, useRef } from "react";
import useNotification from "../../hooks/useNotification";
import useTranslations from "../../hooks/useTranslations";
import { Button } from "../wombat-ui";
import { DocumentPlate } from "../utils/DocumentPlate";
import { GetReviewFullDocument, ReviewFullFragment, useAddReviewDocument } from '../../graphql/generated/definitions';
import useDeleteReviewDocument from '../../hooks/useDeleteReviewDocument';
import useDirectUpload from "../../hooks/useDirectUpload";

type UserFollowUpDocumentsProps = {
  review: ReviewFullFragment;
};

const UserFollowUpDocuments: React.FC<UserFollowUpDocumentsProps> = ({
  review,
}) => {
  const t = useTranslations();

  const fileUploadField = useRef(null);

  const [
    addReviewDocument,
    { loading: addReviewDocumentLoading },
  ] = useAddReviewDocument({
    refetchQueries: [{ query: GetReviewFullDocument, variables: { id: review.id } }],
    awaitRefetchQueries: true
  });
  const { notifyError } = useNotification();

  const [
    deleteReviewDocument,
  ] = useDeleteReviewDocument({
    refetchQueries: [{ query: GetReviewFullDocument, variables: { id: review.id } }],
    awaitRefetchQueries: true
  });

  const handleDelete = useCallback(({ id }) => {
    deleteReviewDocument({
      variables: { input: { id } },
    })
  }, [deleteReviewDocument]);

  const [ upload, { loading: directUploadLoading} ] = useDirectUpload();

  const handleUploadDocument = useCallback(async (e) => {
    try {
      const { signed_id: signedId } = await upload(e.target?.files?.[0]);
      addReviewDocument({
        variables: {
          input: {
            id: review.id,
            signedId
          },
        },
      });
    } catch (e) {
      notifyError({
        title: t("forms.errors.notificationTitle") as string,
        description: t("forms.errors.invalidFile") as string,
      });
    }
  }, [review.id]);

  const addDocumentButton = (
    <div className="flex justify-center pt-3">
      <Button
        variant='subtle'
        onClick={() => fileUploadField?.current?.click()}
        disabled={addReviewDocumentLoading || directUploadLoading}
        loading={addReviewDocumentLoading || directUploadLoading}
        id="uploadDocumentButton"
      >
        {t("reviews.addDocument")}
      </Button>
    </div>
  );

  return (
    <div className="-mt-2 sm:col-span-2">
      <dt className="flex items-center justify-between text-sm font-medium text-light-900">
        <span>Documents</span>
        <div>
          <input
            ref={fileUploadField}
            type="file"
            onChange={handleUploadDocument}
            className="absolute inset-0 w-px h-px p-0 m-0 opacity-0"
            name="document"
          />
        </div>
      </dt>
      <dd className="text-sm text-dark-100">
        {review.documents.length === 0 ? (
          <div className="flex justify-center py-2 text-sm text-dark-100">
            <div className="flex flex-col space-y-3">
              <p className="text-light-900">{t("reviews.noDocuments")}</p>
              {
                review.canAddDocument && addDocumentButton
              }
            </div>
          </div>
        ) : (
          <>
            <ul className="pl-3 pr-4 mt-1 border border-gray-200 divide-y divide-gray-200 rounded-md" id="documentPlates">
              {review.documents.map((document) => (
                <DocumentPlate
                  allowDelete={review.canDeleteDocument}
                  onDelete={handleDelete}
                  document={document}
                  key={document.id}
                />
              ))}
            </ul>
            {
              review.canAddDocument && addDocumentButton
            }
          </>
        )}
      </dd>
    </div>
  );
};

export default UserFollowUpDocuments;
